import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import { useIsAdmin } from '../api/auth';

interface Overview {
  window: string;
  website: {
    views: number;
    visitors: number;
    enquiries: number;
    conversionRate: number;
    series: Array<{ t: string; views: number; visitors: number }>;
    pages: Array<{ path: string; count: number }>;
    referrers: Array<{ name: string; count: number }>;
    devices: Array<{ name: string; count: number }>;
  };
  product: {
    inspections: number;
    annotations: number;
    operators: number;
    modelsTrained: number;
    views: Array<{ path: string; count: number; users: number }>;
  };
}

const WINDOWS = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

/** Website reach and product usage, from our own cookieless data. */
export function AnalyticsView() {
  const isAdmin = useIsAdmin();
  const [window, setWindow] = useState('7d');
  const [data, setData] = useState<Overview | null>(null);

  const load = useCallback(() => {
    api.analytics(window)
      .then((d) => setData(d as unknown as Overview))
      .catch(() => setData(null));
  }, [window]);

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [load]);

  if (!isAdmin) {
    return (
      <div>
        <h1 className="view-title">Analytics</h1>
        <div className="panel muted">Analytics requires the ADMIN role.</div>
      </div>
    );
  }

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 className="view-title">Analytics</h1>
        <select value={window} onChange={(e) => setWindow(e.target.value)}>
          {WINDOWS.map((w) => (
            <option key={w.value} value={w.value}>
              {w.label}
            </option>
          ))}
        </select>
      </div>

      {!data ? (
        <div className="panel spinner">Loading…</div>
      ) : (
        <div className="grid" style={{ gap: 20 }}>
          <div>
            <h2 style={{ fontSize: 20, margin: '0 0 12px' }}>Website</h2>
            <div className="row" style={{ gap: 16, alignItems: 'stretch' }}>
              <Tile label="Visitors" value={data.website.visitors} big />
              <Tile label="Page views" value={data.website.views} />
              <Tile label="Enquiries" value={data.website.enquiries} tone="var(--good)" />
              <Tile
                label="Conversion"
                value={`${(data.website.conversionRate * 100).toFixed(1)}%`}
                tone="var(--accent)"
              />
            </div>
          </div>

          <div className="panel">
            <strong>Visitors over time</strong>
            <TrendChart series={data.website.series} />
          </div>

          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
            <RankList title="Top pages" items={data.website.pages.map((p) => ({ name: p.path, count: p.count }))} />
            <RankList title="Where visitors come from" items={data.website.referrers} />
            <RankList title="Devices" items={data.website.devices} />
          </div>

          <div>
            <h2 style={{ fontSize: 20, margin: '8px 0 12px' }}>Product usage</h2>
            <div className="row" style={{ gap: 16, alignItems: 'stretch' }}>
              <Tile label="Inspections" value={data.product.inspections} big />
              <Tile label="Annotations" value={data.product.annotations} />
              <Tile label="Active operators" value={data.product.operators} />
              <Tile label="Models trained" value={data.product.modelsTrained} />
            </div>
          </div>

          <div className="panel">
            <strong>Most-used screens</strong>
            <p className="muted" style={{ fontSize: 12, margin: '4px 0 12px' }}>
              Which parts of the app your team actually works in.
            </p>
            <RankList
              bare
              items={data.product.views.map((v) => ({ name: `${v.path} (${v.users} users)`, count: v.count }))}
            />
          </div>

          <div className="panel muted" style={{ fontSize: 12 }}>
            Cookieless: visitors are counted with a hash of IP + browser that rotates daily and is
            never stored as an address, so nobody can be followed across days — which is why the
            site still needs no consent banner.
          </div>
        </div>
      )}
    </div>
  );
}

function Tile({
  label,
  value,
  tone,
  big,
}: {
  label: string;
  value: string | number;
  tone?: string;
  big?: boolean;
}) {
  return (
    <div className="stat-tile" style={{ flex: '1 1 160px' }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ fontSize: big ? 46 : 34, color: tone }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

/** Visitors and views over time — two thin lines, no second axis. */
function TrendChart({ series }: { series: Array<{ t: string; views: number; visitors: number }> }) {
  if (series.length === 0) {
    return <div className="muted" style={{ padding: '28px 0' }}>No traffic in this window yet.</div>;
  }
  const W = 720;
  const H = 180;
  const PAD = 34;
  const max = Math.max(1, ...series.map((s) => Math.max(s.views, s.visitors)));
  const x = (i: number) => PAD + (i / Math.max(1, series.length - 1)) * (W - PAD - 8);
  const y = (v: number) => H - 24 - (v / max) * (H - 44);
  const path = (key: 'views' | 'visitors') =>
    series.map((s, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(s[key])}`).join(' ');

  return (
    <div>
      <div className="row" style={{ gap: 16, margin: '8px 0' }}>
        <span className="row" style={{ gap: 6 }}>
          <span style={{ width: 12, height: 2, background: 'var(--accent)' }} />
          <span className="muted" style={{ fontSize: 13 }}>Page views</span>
        </span>
        <span className="row" style={{ gap: 6 }}>
          <span style={{ width: 12, height: 2, background: 'var(--good)' }} />
          <span className="muted" style={{ fontSize: 13 }}>Visitors</span>
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H }} role="img"
        aria-label="Visitors and page views over time">
        {[0, max / 2, max].map((t) => (
          <g key={t}>
            <line x1={PAD} x2={W} y1={y(t)} y2={y(t)} stroke="var(--border)" strokeWidth={1} />
            <text x={PAD - 6} y={y(t) + 4} textAnchor="end" fontSize={11} fill="var(--text-dim)">
              {Math.round(t)}
            </text>
          </g>
        ))}
        <path d={path('views')} fill="none" stroke="var(--accent)" strokeWidth={2} strokeLinejoin="round" />
        <path d={path('visitors')} fill="none" stroke="var(--good)" strokeWidth={2} strokeLinejoin="round" />
      </svg>
    </div>
  );
}

function RankList({
  title,
  items,
  bare,
}: {
  title?: string;
  items: Array<{ name: string; count: number }>;
  bare?: boolean;
}) {
  const max = Math.max(1, ...items.map((i) => i.count));
  const body = (
    <div className="grid" style={{ gap: 8 }}>
      {items.map((i) => (
        <div key={i.name}>
          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 3 }}>
            <span style={{ fontSize: 14 }}>{i.name}</span>
            <strong style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>{i.count}</strong>
          </div>
          <div style={{ background: 'var(--bg)', borderRadius: 4, height: 8 }}>
            <div
              style={{
                width: `${(i.count / max) * 100}%`,
                height: '100%',
                background: 'var(--accent)',
                borderRadius: '0 4px 4px 0',
                minWidth: 3,
              }}
            />
          </div>
        </div>
      ))}
      {items.length === 0 && <span className="muted">Nothing recorded yet.</span>}
    </div>
  );
  if (bare) return body;
  return (
    <div className="panel">
      <strong>{title}</strong>
      <div style={{ marginTop: 12 }}>{body}</div>
    </div>
  );
}
